import { useCallback, useState } from "react";
import useTabStore from "@/stores/useTabStore";

type Selections = Record<string, string[]>;
const EMPTY: Selections = {};

/**
 * Manages filter/highlight selections for a widget.
 *
 * When `persistKey` is provided, reads/writes from `DashboardTab.widgetFilters`
 * in `useTabStore` (server-synced). When `persistKey` is undefined, falls back
 * to local component state (e.g. standalone widget on the Portfolio Builder page).
 */
export function useWidgetFilterState(
  persistKey: string | undefined,
  kind: "filter" | "highlight"
): [Selections, React.Dispatch<React.SetStateAction<Selections>>] {
  // Fallback: local state for widgets rendered outside a tab (no persistKey)
  const [localSelections, setLocalSelections] = useState<Selections>(EMPTY);

  const storeSelections = useTabStore(
    useCallback(
      (state) => {
        if (!persistKey) return EMPTY;
        const tab = state.tabs.find((t) => t.id === state.activeTabId);
        return tab?.widgetFilters?.[persistKey]?.[kind] ?? EMPTY;
      },
      [persistKey, kind]
    )
  );

  const setStoreSelections = useCallback(
    (value: React.SetStateAction<Selections>) => {
      if (!persistKey) return;
      const store = useTabStore.getState();
      const action =
        kind === "filter" ? store.setWidgetFilter : store.setWidgetHighlight;

      if (typeof value === "function") {
        const tab = store.tabs.find((t) => t.id === store.activeTabId);
        const current = tab?.widgetFilters?.[persistKey]?.[kind] ?? EMPTY;
        action(persistKey, value(current));
      } else {
        action(persistKey, value);
      }
    },
    [persistKey, kind]
  );

  if (!persistKey) {
    return [localSelections, setLocalSelections];
  }

  return [storeSelections, setStoreSelections];
}
